<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import type {RouteLocationRaw} from 'vue-router';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import RecommendedPill from '@/vue/atoms/recommended-pill.vue';
import ConsumedMarker from '@/vue/atoms/consumed-marker.vue';
import MediaPlayBadge from '@/vue/atoms/media-play-badge.vue';
import CompanyBadge from '@/vue/atoms/company-badge.vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {pagesPath} from '@/ts/router/pages-path';
import {LibraryContentTypeDTO, type LibraryMediaTypeDTO, type ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {
  contentTypeLabelKey, isPlayableMediaType, mediaLabelWithDuration, mediaTypeLabelKey, statusMarkerLabelKey,
} from '@/ts/utils/recco-label-keys';

const props = defineProps<{
  item: ExploreLibraryItemDTO;
  backLabel: string;
  // Flat collections (the topic grid) carry no section headings, so the per-card content-type badge
  // that distinguishes a challenge inside an otherwise-educational rail has nothing to contrast
  // with. There every card reads the same way: one media label and the title, per the mockup.
  uniformLabels?: boolean;
}>();

const {t: $t} = useI18n();

const title = computed<string>(() =>
  props.item.content?.name
  ?? props.item.challenge?.title
  ?? $t('untitled'));

const imageUrl = computed<string | null>(() => {
  if (props.item.content?.image) {
    return calculateMediaUrl(props.item.content.image);
  }
  // Challenges provide an absolute URL.
  return props.item.challenge?.imageUrl ?? null;
});

// Educational content carries a single media type; a challenge carries the formats marked on it
// (IPD-3047) and takes the first, since one card shows one verb.
const mediaLabel = computed<string | null>(() => {
  if (props.item.contentType === LibraryContentTypeDTO.Educational) {
    // With the length behind it, "Listen · 12 min" (IPD-3375); an article's stays the verb alone.
    return props.item.mediaType
      ? mediaLabelWithDuration($t(mediaTypeLabelKey[props.item.mediaType]), props.item.content?.duration, $t)
      : null;
  }
  if (!props.uniformLabels) {
    return null;
  }
  const formats: LibraryMediaTypeDTO[] = props.item.mediaTypes ?? [];

  return formats.length > 0 ? $t(mediaTypeLabelKey[formats[0]]) : null;
});

const contentTypeLabel = computed<string>(() => $t(contentTypeLabelKey[props.item.contentType]));

// Audio/video items carry the same play badge as the recommendation band, so one item does not
// look different depending on the rail it sits in (IPD-3036). Gated on educational for the same
// reason as mediaLabel: challenges have no media type.
const showPlayBadge = computed<boolean>(() =>
  props.item.contentType === LibraryContentTypeDTO.Educational && isPlayableMediaType(props.item.mediaType));

// Educational cards drop the badge — the rail they sit in already says it, so it read as
// repetitive (IPD-2998). Challenges keep theirs in a rail, where they are mixed into otherwise
// educational items and the type is not obvious. In a flat collection nothing is contrasted, so no
// card carries one.
const contentTypeBadge = computed<string | null>(() =>
  props.uniformLabels || props.item.contentType === LibraryContentTypeDTO.Educational
    ? null
    : contentTypeLabel.value);

const categoryLabel = computed<string>(() =>
  mediaLabel.value ? `${mediaLabel.value} - ${contentTypeLabel.value}` : contentTypeLabel.value);

// Mirrors the visual marker precedence: consumed replaces "Recommended" and subsumes the media verb.
const ariaLabel = computed<string>(() => {
  if (props.item.consumed && props.item.mediaType) {
    return `${$t(statusMarkerLabelKey[props.item.mediaType])}, ${title.value} - ${contentTypeLabel.value}`;
  }
  const prefix = props.item.recommended ? `${$t('recco_recommended')}, ` : '';
  return `${prefix}${title.value} - ${categoryLabel.value}`;
});

const route = computed<RouteLocationRaw | null>(() => {
  if (props.item.challenge) {
    return {name: pagesPath.content.challenge, params: {id: String(props.item.challenge.id)}};
  }
  // Guard the one-of invariant: an item with neither a challenge nor a content id
  // would otherwise link to /block-content/ with an empty param. Render a non-link instead.
  const contentId = props.item.content?.id;
  if (!contentId) {
    return null;
  }
  return {
    name: 'block-content',
    params: {itemId: contentId},
    state: {backLabel: props.backLabel},
  };
});
</script>

<template>
  <component
    :is="route ? 'router-link' : 'div'"
    :to="route ?? undefined"
    class="library-card"
    :aria-label="ariaLabel"
  >
    <div class="card-image">
      <lazy-image
        v-if="imageUrl"
        :src="imageUrl"
        :alt="title"
        :width="246"
        format="webp"
        class="image"
      />
      <media-play-badge v-if="showPlayBadge"/>
      <!-- Consumed marker takes precedence over the "Recommended" pill (design). -->
      <consumed-marker v-if="item.consumed && item.mediaType" :media-type="item.mediaType"/>
      <recommended-pill v-else-if="item.recommended"/>
      <!-- Partner corner banner (e.g. Allianz), same as the recommendation band cards -->
      <company-badge :company-image="item.content?.companyImage"/>
    </div>
    <div class="card-info">
      <!-- Educational items have a media verb and no badge; challenges the opposite — so the
           row is skipped entirely when neither is present, instead of rendering empty. -->
      <div v-if="mediaLabel || contentTypeBadge" class="card-category">
        <span v-if="mediaLabel">{{ mediaLabel }}</span>
        <span v-if="contentTypeBadge" class="type-badge">{{ contentTypeBadge }}</span>
      </div>
      <h3 class="card-title">
        {{ title }}
      </h3>
    </div>
  </component>
</template>

<style lang="scss" scoped>
.library-card {
  flex: 0 0 200px;
  border-radius: var(--card-border-radius);
  overflow: hidden;
  transition: transform 0.2s ease;
  text-decoration: none;
  color: inherit;
  display: block;

  &:hover {
    transform: translateY(-2px);
  }

  @include focus-visible;

  @include screen-md {
    flex: 0 0 246px;
  }
}

.card-image {
  position: relative;
  width: 100%;
  height: 150px;
  background-color: var(--Neutral-UI-neutral-ui-10);
  border-radius: var(--card-border-radius);
  overflow: hidden;

  @include screen-md {
    height: var(--Spacing-spacing-12, 188px);
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-info {
  padding: var(--Spacing-spacing-4) 0 6px;
}

.card-category {
  @include font-body-sm;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-2);
  color: var(--Neutral-UI-neutral-ui-60);
}

.type-badge {
  @include explore-content-type-badge;
}

.card-title {
  @include font-body-sm;
  color: var(--Neutral-UI-neutral-ui-80);
  margin: 0;
  line-height: 1.4;
  font-weight: 600;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}
</style>
